/**
 * CalqTool logo — header: logo 3 (licht/donker PNG, SVG-fallback); footer: logo 2 licht, logo 3 donker in dark mode.
 */
import { useState } from 'react';
import { useThemeOptional } from '../lib/ThemeContext';

interface LogoProps {
  variant?: 'header' | 'footer' | 'icon';
  className?: string;
  size?: number;
}

function assetPath(fileName: string): string {
  return encodeURI(`/assets/${fileName}`);
}

const HEADER_PNG = {
  light: assetPath('CalqTool logo 3 met tm.png'),
  dark: assetPath('CalqTool logo 3 met tm dark.png'),
} as const;

const HEADER_SVG = {
  light: encodeURI('/assets/CalqTool-logo-3-tm-light.svg'),
  dark: encodeURI('/assets/CalqTool-logo-3-tm-dark.svg'),
} as const;

const FOOTER_PNG_LIGHT = assetPath('CalqTool logo 2 met tm.png');

const ICON_SRC = '/assets/Favicon.png';

export function Logo({ variant = 'icon', className = '', size = 36 }: LogoProps) {
  const theme = useThemeOptional();
  const resolved = theme?.resolved ?? 'light';
  const isDark = resolved === 'dark';

  const [headerFallback, setHeaderFallback] = useState(false);
  const [footerFallback, setFooterFallback] = useState(false);

  if (variant === 'icon') {
    return (
      <img
        src={ICON_SRC}
        alt="CalqTool™ logo"
        width={size}
        height={size}
        className={className}
        style={{ objectFit: 'contain', width: size, height: size }}
      />
    );
  }

  if (variant === 'header') {
    const png = isDark ? HEADER_PNG.dark : HEADER_PNG.light;
    const svg = isDark ? HEADER_SVG.dark : HEADER_SVG.light;
    const src = headerFallback ? svg : png;
    return (
      <img
        src={src}
        alt="CalqTool™ logo"
        height={size}
        className={className}
        style={{ objectFit: 'contain', height: size, width: 'auto' }}
        onError={() => setHeaderFallback(true)}
      />
    );
  }

  /* footer */
  const png = isDark ? HEADER_PNG.dark : FOOTER_PNG_LIGHT;
  const svg = isDark ? HEADER_SVG.dark : HEADER_SVG.light;
  const src = footerFallback ? svg : png;
  return (
    <img
      src={src}
      alt="CalqTool™ logo"
      height={size}
      className={className}
      style={{ objectFit: 'contain', height: size, width: 'auto' }}
      onError={() => setFooterFallback(true)}
    />
  );
}
